import React, { useState, useEffect } from 'react';
import { Calendar, Loader2, AlertCircle } from 'lucide-react';
import { formatCurrency } from '../../utils/formatCurrency';
import SortableTableHeader from '../../components/SortableTableHeader';
import { getCustomerBalanceSummary, getCustomerBalanceDetail } from '../../utils/reportApi';
import type { CustomerBalanceSummaryReport, CustomerBalanceDetailReport } from '../../types/report';
import { ReportLayout } from '../../components/reports/ReportLayout';
import { useInvoicePreviewModal } from '../../hooks/useInvoicePreviewModal';

type DetailSortColumn = 'invoice_number' | 'txn_date' | 'due_date' | 'total_amount' | 'balance' | 'status';

const CustomerBalanceDetailView = ({ customerId, asOfDate }: { customerId: number; customerName: string; asOfDate: string }) => {
  const [detail, setDetail] = useState<CustomerBalanceDetailReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { openInvoicePreview, invoicePreviewModal } = useInvoicePreviewModal();

  const [sortColumn, setSortColumn] = useState<DetailSortColumn>('txn_date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const handleSort = (column: DetailSortColumn) => {
    if (sortColumn === column) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDir('asc');
    }
  };

  const sortedInvoices = React.useMemo(() => {
    const invoices = detail?.customers?.[0]?.invoices || [];
    return [...invoices].sort((a, b) => {
      let comparison = 0;
      if (sortColumn === 'invoice_number') comparison = a.invoice_number.localeCompare(b.invoice_number);
      else if (sortColumn === 'txn_date') comparison = String(a.txn_date).localeCompare(String(b.txn_date));
      else if (sortColumn === 'due_date') comparison = String(a.due_date).localeCompare(String(b.due_date));
      else if (sortColumn === 'total_amount') comparison = parseFloat(a.total_amount) - parseFloat(b.total_amount);
      else if (sortColumn === 'balance') comparison = parseFloat(a.balance) - parseFloat(b.balance);
      else if (sortColumn === 'status') comparison = a.status.localeCompare(b.status);
      
      return sortDir === 'asc' ? comparison : -comparison;
    });
  }, [detail, sortColumn, sortDir]);

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        setLoading(true);
        const data = await getCustomerBalanceDetail({ customer_id: customerId, as_of: asOfDate });
        setDetail(data);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load details');
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [customerId, asOfDate]);

  if (loading) {
    return (
      <div className="p-12 text-center">
        <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400 mb-4" />
        <p className="text-slate-500 font-medium">Loading details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <AlertCircle className="w-10 h-10 mx-auto text-red-500 mb-4" />
        <p className="text-red-600 font-medium">{error}</p>
      </div>
    );
  }

  if (!detail || !detail.customers[0]) {
    return <div className="p-8 text-center text-slate-500">No data found.</div>;
  }

  return (
    <>
    <div className="overflow-x-auto">
      <table className="w-full text-sm text-left">
        <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200">
          <tr>
            <SortableTableHeader<DetailSortColumn> label="Invoice #" column="invoice_number" currentSort={sortColumn} sortDir={sortDir} onSort={handleSort} />
            <SortableTableHeader<DetailSortColumn> label="Txn Date" column="txn_date" currentSort={sortColumn} sortDir={sortDir} onSort={handleSort} />
            <SortableTableHeader<DetailSortColumn> label="Due Date" column="due_date" currentSort={sortColumn} sortDir={sortDir} onSort={handleSort} />
            <SortableTableHeader<DetailSortColumn> label="Total Amount" column="total_amount" currentSort={sortColumn} sortDir={sortDir} onSort={handleSort} align="right" />
            <SortableTableHeader<DetailSortColumn> label="Balance" column="balance" currentSort={sortColumn} sortDir={sortDir} onSort={handleSort} align="right" />
            <SortableTableHeader<DetailSortColumn> label="Status" column="status" currentSort={sortColumn} sortDir={sortDir} onSort={handleSort} align="center" />
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {sortedInvoices.length === 0 ? (
            <tr>
              <td colSpan={6} className="px-6 py-8 text-center text-slate-500">
                No open invoices found.
              </td>
            </tr>
          ) : (
            sortedInvoices.map(inv => (
              <tr
                key={inv.invoice_id}
                className="hover:bg-slate-50 transition-colors cursor-pointer"
                onClick={() => openInvoicePreview(inv.invoice_id)}
              >
                <td className="px-6 py-4 font-medium text-blue-700">
                  {inv.invoice_number}
                </td>
                <td className="px-6 py-4 text-slate-600">{inv.txn_date}</td>
                <td className="px-6 py-4 text-slate-600">{inv.due_date}</td>
                <td className="px-6 py-4 text-right text-slate-600">
                  {formatCurrency(parseFloat(inv.total_amount))}
                </td>
                <td className="px-6 py-4 text-right font-medium text-slate-800">
                  {formatCurrency(parseFloat(inv.balance))}
                </td>
                <td className="px-6 py-4 text-center">
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                    {inv.status}
                  </span>
                </td>
              </tr>
            ))
          )}
        </tbody>
        <tfoot className="bg-slate-50 border-t border-slate-200">
          <tr>
            <td colSpan={4} className="px-6 py-4 font-semibold text-slate-900 text-right">
              Total Balance
            </td>
            <td className="px-6 py-4 text-right font-semibold text-slate-900 text-base">
              {formatCurrency(parseFloat(detail.totals.balance))}
            </td>
            <td></td>
          </tr>
        </tfoot>
      </table>
    </div>
    {invoicePreviewModal}
    </>
  );
};

type SummarySortColumn = 'customer' | 'invoices' | 'balance';

const CustomerBalanceSummary = () => {
  const [asOfDate, setAsOfDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [report, setReport] = useState<CustomerBalanceSummaryReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [sortColumn, setSortColumn] = useState<SummarySortColumn>('customer');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const handleSort = (column: SummarySortColumn) => {
    if (sortColumn === column) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDir('asc');
    }
  };

  const sortedCustomers = React.useMemo(() => {
    if (!report) return [];
    return [...report.customers].sort((a, b) => {
      let comparison = 0;
      if (sortColumn === 'customer') {
        comparison = a.display_name.localeCompare(b.display_name);
      } else if (sortColumn === 'invoices') {
        comparison = a.open_invoice_count - b.open_invoice_count;
      } else if (sortColumn === 'balance') {
        comparison = parseFloat(a.balance) - parseFloat(b.balance);
      }
      return sortDir === 'asc' ? comparison : -comparison;
    });
  }, [report, sortColumn, sortDir]);

  // Drill-down state
  const [view, setView] = useState<'summary' | 'detail'>('summary');
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
  const [selectedCustomerName, setSelectedCustomerName] = useState<string | null>(null);

  useEffect(() => {
    if (view === 'summary') {
      const fetchReport = async () => {
        try {
          setLoading(true);
          const data = await getCustomerBalanceSummary({ as_of: asOfDate });
          setReport(data);
          setError(null);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to load report');
        } finally {
          setLoading(false);
        }
      };
      fetchReport();
    }
  }, [asOfDate, view]);

  const handleDrillDown = (customerId: number, customerName: string) => {
    setSelectedCustomerId(customerId);
    setSelectedCustomerName(customerName);
    setView('detail');
  };

  const handleBackToSummary = () => {
    setView('summary');
    setSelectedCustomerId(null);
    setSelectedCustomerName(null);
  };

  const filters = (
    <div className="flex flex-col sm:flex-row sm:items-end gap-4">
      <div className="flex-1 max-w-xs">
        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
          As of Date
        </label>
        <div className="relative">
          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="date"
            value={asOfDate}
            onChange={(e) => {
              setAsOfDate(e.target.value);
              if (view === 'detail') handleBackToSummary();
            }}
            className="w-full pl-10 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-800/20 focus:border-slate-400"
          />
        </div>
      </div>
    </div>
  );

  return (
    <ReportLayout
      title={view === 'summary' ? 'Customer Balance Summary' : `Customer Balance Detail`}
      description={view === 'summary' ? 'View balances for all customers up to a specific date.' : `Open invoices for ${selectedCustomerName}`}
      filters={filters}
      onBack={view === 'detail' ? handleBackToSummary : undefined}
      backLabel="Back to summary"
      onExport={() => alert('Export functionality to be implemented')}
      onPrint={() => window.print()}
    >
      {view === 'summary' ? (
        loading ? (
          <div className="p-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400 mb-4" />
            <p className="text-slate-500 font-medium">Loading report data...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <AlertCircle className="w-10 h-10 mx-auto text-red-500 mb-4" />
            <p className="text-red-600 font-medium">{error}</p>
          </div>
        ) : !report ? (
          <div className="p-8 text-center text-slate-500">No data available.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200">
                <tr>
                  <SortableTableHeader<SummarySortColumn> label="Customer" column="customer" currentSort={sortColumn} sortDir={sortDir} onSort={handleSort} />
                  <SortableTableHeader<SummarySortColumn> label="Open Invoices" column="invoices" currentSort={sortColumn} sortDir={sortDir} onSort={handleSort} align="center" />
                  <SortableTableHeader<SummarySortColumn> label="Balance" column="balance" currentSort={sortColumn} sortDir={sortDir} onSort={handleSort} align="right" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sortedCustomers.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-6 py-8 text-center text-slate-500">
                      No customers with open balances found.
                    </td>
                  </tr>
                ) : (
                  sortedCustomers.map(customer => (
                    <tr key={customer.customer_id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4 font-medium text-slate-900">
                        {customer.display_name}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-700 font-medium border border-slate-200">
                          {customer.open_invoice_count}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => handleDrillDown(customer.customer_id, customer.display_name)}
                          className="font-medium text-blue-600 hover:text-blue-800 hover:underline text-base"
                          title="Click to view details"
                        >
                          {formatCurrency(parseFloat(customer.balance))}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot className="bg-slate-50 border-t border-slate-200">
                <tr>
                  <td className="px-6 py-4 font-semibold text-slate-900">
                    Total ({report.totals.customer_count || report.customers.length} Customers)
                  </td>
                  <td></td>
                  <td className="px-6 py-4 text-right font-semibold text-slate-900 text-lg">
                    {formatCurrency(parseFloat(report.totals.balance))}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )
      ) : (
        selectedCustomerId && (
          <CustomerBalanceDetailView 
            customerId={selectedCustomerId} 
            customerName={selectedCustomerName!} 
            asOfDate={asOfDate} 
          />
        )
      )}
    </ReportLayout>
  );
};

export default CustomerBalanceSummary;
